<template>
  <div class="flex flex-col">
    <p
      :class="[
        classes,
        'flex items-center gap-2 space-x-0.5',
        !isPDP ? 'mb-2' : '',
      ]"
      data-testid="price"
    >
      <span
        v-if="!isPDP"
        class="whitespace-nowrap text-md font-semibold leading-none"
        :class="[strikeThroughPrices.length ? 'text-red-700' : 'text-gray-900']"
      >
        {{ formatCurrency(totalPrice) }}
      </span>
      <del
        v-for="(reduction, index) in strikeThroughPrices"
        :key="`${reduction}-${index}`"
        class="whitespace-nowrap text-sm leading-none text-anthracite-550"
        data-testid="initialProductPrice"
      >
        {{ formatCurrency(reduction) }}
      </del>
      <template v-if="showPriceFrom">
        {{ $t('price.starting_from') }}
      </template>
      <span
        v-if="isPDP"
        class="whitespace-nowrap text-md font-semibold leading-none"
        :class="strikeThroughPrices.length ? 'text-red-700' : 'text-gray-900'"
      >
        {{ formatCurrency(totalPrice) }}
      </span>
      <span
        v-if="showTaxInfo"
        class="text-sm text-gray-500 md:ml-1 md:text-left"
        data-testid="tax-info"
      >
        {{ $t('global.including_vat') }}
      </span>
    </p>
    <div
      v-if="coins && currentShop?.coinsActive"
      class="mt-2 flex items-center gap-2"
    >
      <button
        type="button"
        class="flex items-center gap-2 text-md font-medium text-anthracite-450 transition-colors hover:text-orange-500"
        :aria-label="$t('global.coins.faq_title')"
        @click="toggleCoinsFaqSlideIn"
      >
        <span><IconSnipesCoinsoutlined class="size-4 text-orange-500" /></span>
        <span>+ {{ coinsValue }} {{ $t('global.coins.coins') }}</span>
      </button>
    </div>
    <div
      v-if="
        lowestPriorPrice?.withTax &&
        lowestPriorPrice?.relativeDifferenceToPrice !== null
      "
      :class="{ 'text-end': !inline }"
      class="w-full text-sm text-anthracite-550 md:text-sm"
      data-testid="lowest-prior-price"
    >
      {{ $t('price.30_day_best_price') }}
      {{ formatCurrency(lowestPriorPrice.withTax) }}
      <br />
      ({{
        formatPercentage(lowestPriorPrice.relativeDifferenceToPrice, {
          signDisplay: 'exceptZero',
        })
      }})
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type {
  LowestPriorPrice,
  Price,
  Promotion,
} from '@scayle/storefront-nuxt'
import { Size } from '#storefront-ui'
import {
  type BasketItemPrice,
  useProductPrice,
  useCurrentShop,
} from '#storefront/composables'
import { useFormatHelpers } from '~/composables/useFormatHelpers'
import type { OrderPrice } from '~/types/order'
import { COINS_FAQ_SLIDEIN } from '~/constants'
import { useSlideIn } from '~/modules/ui/runtime'
import { useRoute } from '#app/composables/router'

const { toggle: toggleCoinsFaqSlideIn } = useSlideIn(COINS_FAQ_SLIDEIN)
const {
  showTaxInfo = false,
  showPriceFrom = false,
  size = Size.MD,
  type = 'normal',
  price,
  inline = true,
  lowestPriorPrice = undefined,
  coins = false,
} = defineProps<{
  price: Price | BasketItemPrice | OrderPrice
  lowestPriorPrice?: LowestPriorPrice
  promotion?: Promotion | null
  showTaxInfo?: boolean
  showPriceFrom?: boolean
  showBadges?: boolean
  size?: Size
  type?: 'normal' | 'whisper' | 'loud' | 'medium'
  inline?: boolean
  isMobile?: boolean
  absoluteDiscountLabel?: boolean
  discountLabelRight?: boolean
  coins?: boolean
}>()

const { appliedReductions, strikeThroughPrices, totalPrice } = useProductPrice(
  () => price,
)

const { formatCurrency, formatPercentage } = useFormatHelpers()
const currentShop = useCurrentShop()
const route = useRoute()
const isPDP = computed(() => route.path.includes('/p/'))

const coinsValue = computed(() => {
  return Math.floor(totalPrice.value / 100)
})

const classes = computed(() => ({
  'text-xl': size === Size.XL,
  'text-lg': size === Size.LG,
  'text-sm': size === Size.SM,
  'text-base': size === Size.MD,
  'text-xs': size === Size.XS,
  'font-bold': type === 'loud',
  'font-semibold': type === 'whisper',
  'font-normal': type === 'normal',
  'font-medium': type === 'medium',
  'text-product-sale': appliedReductions.value.length,
  'text-end': !inline,
}))
</script>
